import { useState, useEffect } from "react";

interface UseToolCallAccordionProps {
  status: "in-progress" | "succeeded" | "failed" | "aborted";
  isLastMessage?: boolean;
  isChatLoading?: boolean;
}

/**
 * Custom hook to manage accordion state for tool call blocks
 *
 * Logic:
 * - Keep accordion open while tool call is in progress or failed
 * - For succeeded tool calls:
 *   - Keep open if this is the last message and chat is still loading (streaming)
 *   - Collapse otherwise (completed messages or non-streaming)
 *
 * This ensures accordions stay open during active streaming but collapse
 * when the message stream is complete.
 */
export const useToolCallAccordion = ({
  status,
  isLastMessage = false,
  isChatLoading = false,
}: UseToolCallAccordionProps) => {
  const [accordionValue, setAccordionValue] = useState<string>("");

  useEffect(() => {
    if (status === "succeeded") {
      // If this is the last message and chat is still loading, keep accordion open
      if (isLastMessage && isChatLoading) {
        setAccordionValue("tool-content"); // Keep open while streaming
      } else {
        setAccordionValue(""); // Collapse for completed messages or non-last messages
      }
    } else if (status === "in-progress") {
      setAccordionValue("tool-content"); // Keep open while in progress
    } else if (status === "failed") {
      setAccordionValue("tool-content"); // Keep open on failure
    }
  }, [status, isLastMessage, isChatLoading]);

  return {
    accordionValue,
    setAccordionValue,
    isOpen: accordionValue === "tool-content",
  };
};
